import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import { GiTempleGate } from "react-icons/gi";
import SEO from "../components/SEO";
import Loader from "../components/Loader";
import api, { imgUrl } from "../utils/api";
import { buildWhatsAppLink, productEnquiryMessage } from "../utils/whatsapp";
import { SAURABH } from "../utils/siteData";

const CATEGORY_GUIDANCE = {
  "Puja Samagri": {
    use: "Used for daily worship, havan, vrata and special ceremonies as guided by the pandit.",
    care: "Keep items clean, dry and reserved for spiritual use. Confirm quantity and usage before the ceremony.",
  },
  Gemstones: {
    use: "Selected only after horoscope guidance so the stone suits the person's planetary condition.",
    care: "Avoid wearing gemstones without consultation. Ask about metal, finger and auspicious wearing day.",
  },
  Rudraksha: {
    use: "Used for japa, protection and spiritual discipline based on mukhi and purpose.",
    care: "Keep it clean, avoid chemical exposure and follow the wearing guidance shared during enquiry.",
  },
  Yantra: {
    use: "Placed in home, office or puja space after proper energizing and sankalp.",
    care: "Keep the yantra in a clean sacred area and avoid moving it casually after sthapana.",
  },
  "Idols & Murti": {
    use: "Used for home mandir, festival worship or ceremonial installation.",
    care: "Confirm size, placement direction and daily worship requirements before installation.",
  },
  Books: {
    use: "Useful for learning stotra, katha, vrata vidhi and spiritual discipline.",
    care: "Store respectfully in a clean place and use during daily or festival worship.",
  },
  "Astrology Services": {
    use: "Helpful for remedies, timing decisions and selecting spiritually suitable items.",
    care: "Share accurate birth details for better guidance.",
  },
  Other: {
    use: "Recommended based on your pooja, remedy or personal spiritual requirement.",
    care: "Confirm usage, quantity and handling instructions before purchase.",
  },
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((res) => {
        setProduct(res.data);
        setActiveImg(0);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Loader label="Loading product..." />;

  if (notFound || !product) {
    return (
      <div className="text-center py-24">
        <p className="text-2xl font-heading text-maroon-800 mb-4">Product Not Found</p>
        <Link to="/marketplace" className="text-saffron-600 font-semibold">← Back to Marketplace</Link>
      </div>
    );
  }

  const waLink = buildWhatsAppLink(SAURABH.whatsapp, productEnquiryMessage(product));
  const guidance = CATEGORY_GUIDANCE[product.category] || CATEGORY_GUIDANCE.Other;

  return (
    <div className="py-12 bg-white">
      <SEO title={product.name} description={product.shortDescription} path={`/marketplace/${product.slug}`} />

      <div className="max-w-6xl mx-auto px-4">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all">
          <FiArrowLeft /> Back to Marketplace
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image gallery */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-gold-50 border border-gold-100 mb-4">
              {product.images && product.images.length > 0 ? (
                <img src={imgUrl(product.images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-saffron-400"><GiTempleGate size={72} /></div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${activeImg === idx ? "border-saffron-500" : "border-gold-100"}`}
                  >
                    <img src={imgUrl(img)} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <span className="text-xs text-saffron-600 font-semibold uppercase tracking-wide">{product.category}</span>
            <h1 className="font-heading text-3xl text-maroon-800 mt-2 mb-3">{product.name}</h1>

            <div className="flex items-center gap-3 mb-4">
              {product.discountPrice > 0 ? (
                <>
                  <span className="text-2xl font-bold text-maroon-700">₹{product.discountPrice}</span>
                  <span className="text-gray-400 line-through">₹{product.price}</span>
                  <span className="bg-maroon-700 text-gold-200 text-xs font-bold px-2 py-1 rounded-full">
                    {Math.round((1 - product.discountPrice / product.price) * 100)}% OFF
                  </span>
                </>
              ) : (
                <span className="text-2xl font-bold text-maroon-700">₹{product.price}</span>
              )}
            </div>

            <p className="flex items-center gap-2 text-sm mb-6">
              {product.inStock ? (
                <span className="flex items-center gap-1 text-green-600 font-medium"><FiCheckCircle /> In Stock</span>
              ) : (
                <span className="text-red-500 font-medium">Currently Out of Stock</span>
              )}
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">{product.shortDescription}</p>

            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-7 py-3 rounded-full font-semibold shadow transition-colors mb-8"
            >
              <FaWhatsapp size={20} /> Enquire on WhatsApp
            </a>

            <div className="border-t border-gold-100 pt-6">
              <h3 className="font-heading text-lg text-maroon-800 mb-3">Description</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{product.description}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 border-t border-gold-100 pt-6 mt-6">
              <div className="bg-saffron-50 border border-gold-100 rounded-2xl p-5">
                <h3 className="font-heading text-lg text-maroon-800 mb-2">Spiritual Use</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{guidance.use}</p>
              </div>
              <div className="bg-gold-50 border border-gold-100 rounded-2xl p-5">
                <h3 className="font-heading text-lg text-maroon-800 mb-2">Care & Guidance</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{guidance.care}</p>
              </div>
            </div>

            <div className="border-t border-gold-100 pt-6 mt-6">
              <h3 className="font-heading text-lg text-maroon-800 mb-2">Authenticity Note</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Items are selected for ritual suitability and can be checked with Pandit Saurabh Shastri before use.
                For remedies, we recommend confirming the item through horoscope guidance before purchase.
              </p>
            </div>

            {product.specifications && product.specifications.length > 0 && (
              <div className="border-t border-gold-100 pt-6 mt-6">
                <h3 className="font-heading text-lg text-maroon-800 mb-3">Specifications</h3>
                <table className="w-full text-sm">
                  <tbody>
                    {product.specifications.map((spec, idx) => (
                      <tr key={idx} className="border-b border-gold-50">
                        <td className="py-2 font-medium text-gray-600 w-1/3">{spec.key}</td>
                        <td className="py-2 text-gray-800">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
